import React, { useState, useEffect } from "react";
import BusinessUnitMatcher from "../BusinessUnitMatcher/BusinessUnitMatcher";
import {
  AlertTriangle,
  Building2,
  RefreshCw,
  Clock,
  Pencil,
  Eye,
  EyeOff,
} from "lucide-react";
import { useNotification } from "@/context/NotificationContext";
import { fetchTrades } from "@/api/fetchTrades/fetchTradesApi";
import { Trade } from "@/api/fetchTrades/types";
import { fetchBusinessUnits } from "@/api/fetchBusinessUnits/fetchBusinessUnitsApi";
import { BusinessUnit } from "@/api/fetchBusinessUnits/types";
import { createBusinessUnitTradeMappings } from "@/api/createBusinessUnitTradeMappings/createBusinessUnitTradeMappingsApi";
import { BusinessUnitTradeMappingRequest } from "@/api/createBusinessUnitTradeMappings/types";
import { getServiceTitanCredential } from "@/api/getServiceTitanCredential/getServiceTitanCredentialApi";
import { ServiceTitanCredential } from "@/api/getServiceTitanCredential/types";
import { saveServiceTitanCredential } from "@/api/saveServiceTitanCredential/saveServiceTitanCredentialApi";
import { triggerBusinessUnitSync } from "@/api/triggerBusinessUnitSync/triggerBusinessUnitSyncApi";
import { fetchBusinessUnitTradeMappings } from "@/api/fetchBusinessUnitTradeMappings/fetchBusinessUnitTradeMappingsApi";
import { BusinessUnitTradeMapping } from "@/api/fetchBusinessUnitTradeMappings/types";

interface BusinessUnitMapping {
  id: string;
  businessUnitId: string;
  businessUnitName: string;
  tradeId: string;
}

interface ServiceTitanIntegrationProps {
  companyUuid?: string;
  companyId?: number;
  isEditMode?: boolean;
}

const ServiceTitanIntegration: React.FC<ServiceTitanIntegrationProps> = ({
  companyUuid,
}) => {
  const { showNotification } = useNotification();
  const [businessUnits, setBusinessUnits] = useState<BusinessUnit[]>([]);
  const [businessUnitsLoading, setBusinessUnitsLoading] = useState(false);
  const [businessUnitsError, setBusinessUnitsError] = useState<string | null>(
    null,
  );

  const [trades, setTrades] = useState<Trade[]>([]);
  const [tradesLoading, setTradesLoading] = useState(true);
  const [tradesError, setTradesError] = useState<string | null>(null);
  const [savingMapping, setSavingMapping] = useState<string | null>(null);

  const [mappings, setMappings] = useState<BusinessUnitMapping[]>([]);

  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [credential, setCredential] = useState<ServiceTitanCredential | null>(
    null,
  );
  const [credentialLoading, setCredentialLoading] = useState(true);
  const [credentialError, setCredentialError] = useState<string | null>(null);

  // Credential editing state
  const [isEditingCredentials, setIsEditingCredentials] = useState(false);
  const [editedTenantId, setEditedTenantId] = useState("");
  const [editedClientId, setEditedClientId] = useState("");
  const [editedClientSecret, setEditedClientSecret] = useState("");
  const [isSavingCredentials, setIsSavingCredentials] = useState(false);
  const [credentialsSaveError, setCredentialsSaveError] = useState<
    string | null
  >(null);
  const [showClientSecret, setShowClientSecret] = useState(false);

  useEffect(() => {
    const loadTrades = async () => {
      try {
        setTradesLoading(true);
        setTradesError(null);
        const response = await fetchTrades();
        setTrades(response.data);
      } catch (error) {
        console.error("Failed to fetch trades:", error);
        setTradesError("Failed to load trade options");
      } finally {
        setTradesLoading(false);
      }
    };

    loadTrades();
  }, [companyUuid]);

  useEffect(() => {
    if (!companyUuid || companyUuid === "0") {
      setCredentialLoading(false);
      return;
    }

    const loadCredential = async () => {
      try {
        setCredentialLoading(true);
        setCredentialError(null);
        const response = await getServiceTitanCredential(companyUuid);
        const cred = response.data.credential;
        setCredential(cred);

        if (cred) {
          setEditedTenantId(cred.tenantId);
          setEditedClientId(cred.clientId);
          setEditedClientSecret(""); // Don't prefill secret for security
          setLastSyncTime(
            cred.updatedAt ? new Date(cred.updatedAt).toLocaleString() : null,
          );
        }
      } catch (error) {
        console.error("Failed to load credential:", error);
        setCredentialError("Failed to load ServiceTitan credentials");
      } finally {
        setCredentialLoading(false);
      }
    };

    loadCredential();
  }, [companyUuid]);

  useEffect(() => {
    if (!credential?.tenantId) {
      setBusinessUnits([]);
      setMappings([]);
      return;
    }

    const loadBusinessUnits = async () => {
      if (!companyUuid) {
        setBusinessUnitsError(
          "Company UUID is required to load business units",
        );
        return;
      }

      try {
        setBusinessUnitsLoading(true);
        setBusinessUnitsError(null);

        const [businessUnitsResponse, mappingsResponse] = await Promise.all([
          fetchBusinessUnits(companyUuid),
          fetchBusinessUnitTradeMappings(companyUuid).catch(() => ({
            data: { mappings: [], total: 0 },
          })),
        ]);

        setBusinessUnits(businessUnitsResponse.data.businessUnits);

        const existingMappingsMap = new Map<number, BusinessUnitTradeMapping>();
        mappingsResponse.data.mappings.forEach((mapping) => {
          existingMappingsMap.set(mapping.businessUnit.id, mapping);
        });

        const newMappings: BusinessUnitMapping[] =
          businessUnitsResponse.data.businessUnits.map((bu) => {
            const existingMapping = existingMappingsMap.get(bu.id);
            return {
              id: `mapping-${bu.id}`,
              businessUnitId: bu.id.toString(),
              businessUnitName: bu.name,
              tradeId: existingMapping
                ? existingMapping.trade.id.toString()
                : "",
            };
          });
        setMappings(newMappings);
      } catch (error) {
        console.error("Failed to fetch business units:", error);
        setBusinessUnitsError("Failed to load business units");
      } finally {
        setBusinessUnitsLoading(false);
      }
    };

    loadBusinessUnits();
  }, [credential]);

  const handleMappingChange = async (
    mappingId: string,
    field: "businessUnitId" | "tradeId",
    value: string,
  ) => {
    setMappings((prev) =>
      prev.map((mapping) =>
        mapping.id === mappingId ? { ...mapping, [field]: value } : mapping,
      ),
    );

    const currentMapping = mappings.find((m) => m.id === mappingId);

    if (currentMapping) {
      const updatedMapping = { ...currentMapping, [field]: value };

      if (
        updatedMapping.businessUnitId &&
        updatedMapping.tradeId &&
        updatedMapping.tradeId !== "" &&
        updatedMapping.businessUnitId !== ""
      ) {
        await saveMapping(updatedMapping, mappingId);
      }
    }
  };

  const saveMapping = async (
    mapping: BusinessUnitMapping,
    mappingId: string,
  ) => {
    if (!companyUuid) return;

    try {
      setSavingMapping(mappingId);

      const apiMapping: BusinessUnitTradeMappingRequest = {
        businessUnitId: parseInt(mapping.businessUnitId),
        tradeId: parseInt(mapping.tradeId),
      };

      const response = await createBusinessUnitTradeMappings(companyUuid, {
        mappings: [apiMapping],
      });

      setLastSyncTime("just now");
    } catch (error: any) {
      console.error("Failed to save mapping:", error);
      console.error("Error details:", error.response?.data || error.message);
    } finally {
      setSavingMapping(null);
    }
  };

  const handleRefreshBusinessUnits = async () => {
    if (!credential?.tenantId || !companyUuid) return;

    setIsRefreshing(true);
    try {
      setBusinessUnitsError(null);

      await triggerBusinessUnitSync(companyUuid);

      const [businessUnitsResponse, mappingsResponse] = await Promise.all([
        fetchBusinessUnits(companyUuid),
        fetchBusinessUnitTradeMappings(companyUuid).catch(() => ({
          data: { mappings: [], total: 0 },
        })),
      ]);

      setBusinessUnits(businessUnitsResponse.data.businessUnits);

      const existingMappingsMap = new Map<number, BusinessUnitTradeMapping>();
      mappingsResponse.data.mappings.forEach((mapping) => {
        existingMappingsMap.set(mapping.businessUnit.id, mapping);
      });

      const currentMappingsMap = new Map(
        mappings.map((m) => [m.businessUnitId, m.tradeId]),
      );

      const newMappings: BusinessUnitMapping[] =
        businessUnitsResponse.data.businessUnits.map((bu) => {
          const currentTradeId = currentMappingsMap.get(bu.id.toString());
          const existingMapping = existingMappingsMap.get(bu.id);

          return {
            id: `mapping-${bu.id}`,
            businessUnitId: bu.id.toString(),
            businessUnitName: bu.name,
            tradeId:
              currentTradeId ||
              (existingMapping ? existingMapping.trade.id.toString() : ""),
          };
        });
      setMappings(newMappings);
      setLastSyncTime("just now");
    } catch (error) {
      console.error("Failed to refresh business units:", error);
      setBusinessUnitsError("Failed to refresh business units");
    } finally {
      setIsRefreshing(false);
    }
  };

  const incompleteMappings = mappings.filter(
    (m) => !m.businessUnitId || !m.tradeId || m.tradeId === "",
  ).length;

  const handleSaveCredentials = async () => {
    if (!companyUuid) return;

    setIsSavingCredentials(true);
    setCredentialsSaveError(null);

    try {
      const saveRequest = {
        tenantId: editedTenantId,
        clientId: editedClientId,
        clientSecret: editedClientSecret,
      };

      const response = await saveServiceTitanCredential(
        companyUuid,
        saveRequest,
      );

      if (response.success && response.credential) {
        // Update local credential state
        setCredential(response.credential);
        setIsEditingCredentials(false);
        setLastSyncTime(new Date().toLocaleString());
        setShowClientSecret(false);

        // Show success notification
        showNotification(
          "Credentials Saved",
          "ServiceTitan credentials have been saved and verified successfully!",
          "success",
        );

        // Trigger business unit sync after successful credential save
        if (companyUuid) {
          await triggerBusinessUnitSync(companyUuid);
          // Reload both business units and trade mappings
          const [businessUnitsResponse, mappingsResponse] = await Promise.all([
            fetchBusinessUnits(companyUuid),
            fetchBusinessUnitTradeMappings(companyUuid).catch(() => ({
              data: { mappings: [], total: 0 },
            })),
          ]);

          setBusinessUnits(businessUnitsResponse.data.businessUnits);

          // Build mappings with existing trade assignments
          const existingMappingsMap = new Map<
            number,
            BusinessUnitTradeMapping
          >();
          mappingsResponse.data.mappings.forEach((mapping) => {
            existingMappingsMap.set(mapping.businessUnit.id, mapping);
          });

          const newMappings: BusinessUnitMapping[] =
            businessUnitsResponse.data.businessUnits.map((bu) => {
              const existingMapping = existingMappingsMap.get(bu.id);
              return {
                id: `mapping-${bu.id}`,
                businessUnitId: bu.id.toString(),
                businessUnitName: bu.name,
                tradeId: existingMapping
                  ? existingMapping.trade.id.toString()
                  : "",
              };
            });
          setMappings(newMappings);
        }
      }
    } catch (error: any) {
      console.error("Failed to save credentials:", error);
      const errorMessage =
        error.response?.data?.error ||
        "Failed to save credentials. Please check your credentials and try again.";
      setCredentialsSaveError(errorMessage);
    } finally {
      setIsSavingCredentials(false);
    }
  };

  const handleCancelEdit = () => {
    if (credential) {
      setEditedTenantId(credential.tenantId);
      setEditedClientId(credential.clientId);
      setEditedClientSecret("");
    } else {
      setEditedTenantId("");
      setEditedClientId("");
      setEditedClientSecret("");
    }
    setIsEditingCredentials(false);
    setCredentialsSaveError(null);
    setShowClientSecret(false);
  };

  return (
    <div className="space-y-6 min-h-[600px]">
      {/* Error Message for Credentials Save */}
      {credentialsSaveError && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <span className="font-medium text-red-800 dark:text-red-300">
                Credential Error:{" "}
              </span>
              <span className="text-red-700 dark:text-red-400">
                {credentialsSaveError}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Connected Account Section */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <Building2 className="h-5 w-5 mr-2 text-gray-600 dark:text-slate-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Connected Account
            </h3>
          </div>
          {!isEditingCredentials && (
            <button
              className="inline-flex items-center px-3 py-1 text-sm font-medium text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-slate-700 rounded-md transition-colors"
              onClick={() => setIsEditingCredentials(true)}
              title="Edit credentials"
            >
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </button>
          )}
        </div>

        {isEditingCredentials ? (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                Tenant ID
              </label>
              <input
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={isSavingCredentials}
                onChange={(e) => setEditedTenantId(e.target.value)}
                placeholder="Enter tenant ID"
                type="text"
                value={editedTenantId}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                Client ID
              </label>
              <input
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={isSavingCredentials}
                onChange={(e) => setEditedClientId(e.target.value)}
                placeholder="Enter client ID"
                type="text"
                value={editedClientId}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                Client Secret
              </label>
              <div className="relative">
                <input
                  className="w-full px-3 py-2 pr-10 text-sm border border-gray-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-gray-900 dark:text-slate-100 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={isSavingCredentials}
                  onChange={(e) => setEditedClientSecret(e.target.value)}
                  placeholder="Enter client secret"
                  type={showClientSecret ? "text" : "password"}
                  value={editedClientSecret}
                />
                <button
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200"
                  disabled={isSavingCredentials}
                  onClick={() => setShowClientSecret(!showClientSecret)}
                  type="button"
                >
                  {showClientSecret ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={
                  isSavingCredentials ||
                  !editedTenantId ||
                  !editedClientId ||
                  !editedClientSecret
                }
                onClick={handleSaveCredentials}
              >
                {isSavingCredentials
                  ? "Verifying & Saving..."
                  : "Save & Verify"}
              </button>
              <button
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 bg-gray-100 dark:bg-slate-700 rounded-md hover:bg-gray-200 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isSavingCredentials}
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div>
                <span className="text-sm text-gray-500 dark:text-slate-400">
                  Tenant ID:
                </span>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {credential?.tenantId || "Not configured"}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500 dark:text-slate-400">
                  Client ID:
                </span>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {credential?.clientId || "Not configured"}
                </p>
              </div>
              <div>
                <span className="text-sm text-gray-500 dark:text-slate-400">
                  Client Secret:
                </span>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {credential?.hasClientSecret ? "••••••••" : "Not configured"}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center text-sm text-gray-500 dark:text-slate-400">
                <Clock className="h-4 w-4 mr-1" />
                <span>Last Sync: {lastSyncTime || "Never"}</span>
              </div>

              <button
                className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-slate-300 bg-white dark:bg-slate-700 hover:bg-gray-50 dark:hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isRefreshing || !credential?.tenantId}
                onClick={handleRefreshBusinessUnits}
              >
                <RefreshCw
                  className={`h-4 w-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`}
                />
                Refresh Business Units
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Business Units Section */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 p-6">
        <div className="mb-4">
          <div className="flex items-center mb-2">
            <Building2 className="h-5 w-5 mr-2 text-gray-600 dark:text-slate-400" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Business Units Found: {businessUnits.length}
            </h3>
          </div>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Map each business unit to a trade category
          </p>
        </div>

        {/* Error Messages */}
        {tradesError && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <span className="font-medium text-red-800 dark:text-red-300">
                  Trade Loading Error:
                </span>
                <span className="text-red-700 dark:text-red-400 ml-1">
                  {tradesError}
                </span>
              </div>
            </div>
          </div>
        )}
        {businessUnitsError && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <span className="font-medium text-red-800 dark:text-red-300">
                  Business Units Error:
                </span>
                <span className="text-red-700 dark:text-red-400 ml-1">
                  {businessUnitsError}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Warning Message */}
        {incompleteMappings > 0 && (
          <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
            <div className="flex">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <span className="font-medium text-yellow-800 dark:text-yellow-300">
                  {incompleteMappings} business units need mapping:
                </span>
                <span className="text-yellow-700 dark:text-yellow-400 ml-1">
                  Use the dropdown menus to assign each business unit to a trade
                  category. For example, &quot;Residential Multi-Trade&quot;
                  might map to &quot;Other&quot; or &quot;Emergency Repair
                  Services&quot; could map to &quot;Plumbing&quot; if they
                  primarily handle plumbing emergencies.
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Business Units Table */}
        <div className="overflow-auto shadow ring-1 ring-black ring-opacity-5 dark:ring-slate-700 md:rounded-lg">
          <table className="min-w-full divide-y divide-gray-300 dark:divide-slate-700">
            <thead className="bg-gray-50 dark:bg-slate-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-300 uppercase tracking-wider">
                  Business Unit
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-300 uppercase tracking-wider">
                  Trade
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
              {businessUnitsLoading ? (
                <tr>
                  <td
                    className="px-6 py-4 text-center text-sm text-gray-500 dark:text-slate-400"
                    colSpan={2}
                  >
                    Loading business units...
                  </td>
                </tr>
              ) : tradesLoading ? (
                <tr>
                  <td
                    className="px-6 py-4 text-center text-sm text-gray-500 dark:text-slate-400"
                    colSpan={2}
                  >
                    Loading trade options...
                  </td>
                </tr>
              ) : mappings.length === 0 ? (
                <tr>
                  <td
                    className="px-6 py-4 text-center text-sm text-gray-500 dark:text-slate-400"
                    colSpan={2}
                  >
                    {credential?.tenantId
                      ? "No business units found for this tenant"
                      : "Configure ServiceTitan credentials to see business units"}
                  </td>
                </tr>
              ) : (
                mappings.map((mapping) => (
                  <BusinessUnitMatcher
                    businessUnitName={mapping.businessUnitName}
                    isSaving={savingMapping === mapping.id}
                    key={mapping.id}
                    mappingId={mapping.id}
                    onMappingChange={handleMappingChange}
                    tradeId={mapping.tradeId}
                    trades={trades.map((trade) => ({
                      id: trade.id.toString(),
                      name: trade.name,
                    }))}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ServiceTitanIntegration;
